import { createContext, useContext, useState, useCallback, useEffect } from "react";
import api from "@/lib/api";
import { detectCurrencyFromIP } from "@/lib/detectCurrency";
import { useAuth } from "@/context/AuthContext";

const CurrencyContext = createContext(null);
const STORAGE_KEY = "padel_currency";

function persistToBackend(currency) {
  if (typeof window === "undefined") return;
  if (!localStorage.getItem("padel_token")) return;
  api.put("/me/currency", { currency }).catch(() => {});
}

export function CurrencyProvider({ children }) {
  const { user } = useAuth();
  const [currency, setCurrencyState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored || "MXN";
  });
  const [userPicked, setUserPicked] = useState(() => !!localStorage.getItem(STORAGE_KEY));

  // Async IP-based upgrade once on mount (when no explicit pick yet).
  useEffect(() => {
    if (userPicked) return;
    let cancelled = false;
    detectCurrencyFromIP().then((cur) => {
      if (cancelled || !cur) return;
      if (!localStorage.getItem(STORAGE_KEY)) setCurrencyState(cur);
    });
    return () => { cancelled = true; };
  }, [userPicked]);

  // Sync with the backend user.currency once authenticated (until user toggles).
  useEffect(() => {
    if (!user || userPicked) return;
    const target = user.currency;
    if (typeof target === "string" && target.length === 3 && target !== currency) {
      setCurrencyState(target.toUpperCase());
    }
  }, [user, userPicked, currency]);

  const setCurrency = useCallback((next) => {
    if (!next || typeof next !== "string" || next.length !== 3) return;
    const cur = next.toUpperCase();
    setCurrencyState(cur);
    localStorage.setItem(STORAGE_KEY, cur);
    setUserPicked(true);
    persistToBackend(cur);
  }, []);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, userPicked }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be inside CurrencyProvider");
  return ctx;
}
